import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { TikTokIcon } from "./icons";
import { AMAZON_STORE, INSTAGRAM_URL, TIKTOK_URL } from "../../data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/our-story", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

const Wordmark = () => (
  <span className="font-serif text-2xl sm:text-[1.7rem] tracking-[0.22em] uppercase leading-none">
    Fashiella
  </span>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled ? "bg-ivory/85 backdrop-blur-md border-b border-black/5 py-4" : "bg-transparent py-6"
        }`}
        data-testid="site-header"
      >
        <nav className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
          <div className="hidden md:flex items-center gap-9 flex-1">
            {links.slice(0, 2).map((l) => (
              <NavLink key={l.to} {...l} active={pathname === l.to} />
            ))}
          </div>

          <Link to="/" data-testid="logo-link" className="md:flex-1 md:text-center">
            <Wordmark />
          </Link>

          <div className="hidden md:flex items-center gap-8 flex-1 justify-end">
            {links.slice(2).map((l) => (
              <NavLink key={l.to} {...l} active={pathname === l.to} />
            ))}
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-instagram-link"
              aria-label="Fashiella on Instagram"
              className="text-charcoal/70 hover:text-rosegold transition-colors duration-300"
            >
              <Instagram size={17} />
            </a>
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-tiktok-link"
              aria-label="Fashiella on TikTok"
              className="text-charcoal/70 hover:text-rosegold transition-colors duration-300"
            >
              <TikTokIcon size={16} />
            </a>
            <a
              href={AMAZON_STORE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-amazon-link"
              className="rounded-full border border-charcoal px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-charcoal hover:text-ivory"
            >
              Amazon
            </a>
          </div>

          <button
            className="md:hidden p-1"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-ivory md:hidden flex flex-col justify-center px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-serif text-5xl tracking-tight py-2 block"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </div>
            <a
              href={AMAZON_STORE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-amazon-link"
              className="mt-10 inline-flex w-fit rounded-full bg-charcoal text-ivory px-7 py-3 text-xs uppercase tracking-[0.2em]"
            >
              Visit Amazon Store
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm text-charcoal/70">
              <Instagram size={16} /> @shopfashiella
            </a>
            <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-charcoal/70">
              <TikTokIcon size={15} /> @shopfashiella
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const NavLink = ({ to, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
    className="group relative text-xs uppercase tracking-[0.2em] py-1"
  >
    {label}
    <span
      className={`absolute left-0 -bottom-0.5 h-px bg-charcoal transition-all duration-500 ${
        active ? "w-full" : "w-0 group-hover:w-full"
      }`}
    />
  </Link>
);
